import { calculateSnappProDisplayPricing } from "@website/accommodation-ui/hotel-card-new/hotel-card-action/hotel-card-pricing/hotel-card-pricing.utils";
import type { VillaListingResponseType } from "@website/accommodation-ui/types/villa/VillaListingType";
import { VillaSearchTextItemType } from "@website/accommodation-ui/villa-search";
import { fetcher } from "@website/fetcher/fetcher";
import { getTracking } from "@website/store/tracking";
import {
  COOKIE_CHANNEL_NAME,
  COOKIE_CHANNEL_NAME_TEMPORARY,
  fixedChannelHosts
} from "@website/utils/channel/constants";
import { fixChannel, getChannel } from "@website/utils/channel/get-channel";
import { getOrigin } from "@website/utils/channel/get-origin";
import { convertChannelPlatform } from "@website/utils/channel/helpers";
import type { Channels } from "@website/utils/channel/types";
import {
  daysBetweenDates,
  isInvalidCheckin,
  today,
  tomorrow
} from "@website/utils/date-format";
import { isSSR } from "@website/utils/is-ssr";
import { replaceOptions } from "@website/utils/replace-options";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { getCMSSeoContent } from "@website/website-ui/services/seo-contents";
import type { CMSSeoContentData } from "@website/website-ui/services/seo-contents/cms-types";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import omitBy from "lodash-es/omitBy";
import pick from "lodash-es/pick";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import type from "markdown";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { ParsedUrlQuery } from "querystring";
import { filterXSS } from "xss";
import { ENDPOINTS } from "../../../constants";
import {
  HotelVillaRateDataType,
  HotelVillaReviewDataType,
  PersianVillaRatings
} from "../../room/[roomId]/index.types";
import {
  CityPagePropertiesType,
  VillaListingCardType,
  VillaListingHotelInfo,
  VillaLocationResponseType,
  VillaSearchTextType
} from "./index.types";
import {
  ResolvedVillaSeoCategory,
  VILLA_LOCATION_ROUTE_QUERY_KEYS,
  VillaSeoCategoryRecord,
  applyVillaSeoCategoryDefault,
  getVillaSeoCategoryBackendSlug,
  getVillaSeoCategoryCanonicalRoute,
  resolveVillaSeoCategory
} from "./seo-category";

export const VILLA_LIST_PAGE_SIZE = 20;
const VILLA_INITIAL_ABOVE_FOLD_IMAGE_COUNT = 4;

export const transformHotelSchema = (
  data: VillaLocationResponseType,
  query: unknown
): VillaListingHotelInfo => {
  const villaTitle =
    data.room_data.title || data.room_data?.property?.title || "";
  const advertisementBadge = data?.room_data?.badges?.find(
    ({ type }) => type === "advertisement"
  ) as VillaLocationResponseType["room_data"]["badges"][0];
  const partnershipBadge = data?.room_data?.badges?.find(
    ({ type }) => type === "partnership"
  ) as VillaLocationResponseType["room_data"]["badges"][0];
  const ordinaryBadges = data?.room_data?.badges?.filter(
    ({ type }) => type === "ordinary"
  ) as VillaLocationResponseType["room_data"]["badges"];
  const { adjustments } = data.room_availability;
  const basePriceOffWithoutSnappPro =
    data.room_availability.base_price_off - (adjustments?.snapp_pro ?? 0);

  return {
    id: data.room_data.id,
    noFollow: Boolean(data.room_data.seo?.noIndex),
    thumbnail: {
      alt: villaTitle,
      url: data.room_data?.image
        ? `${ENDPOINTS.CDN_IMAGES}${data.room_data.image}`
        : data.room_data?.property?.image
          ? `${ENDPOINTS.CDN_IMAGES}${data.room_data.property.image}`
          : ""
    },
    pricing: {
      basePriceOff: basePriceOffWithoutSnappPro,
      basePrice: data?.room_availability?.base_price,
      price: data.room_availability.total_price,
      discountPercentage: 0,
      priceOff: data.room_availability.total_local_price_off
    },
    ...(adjustments ? { adjustments } : {}),
    primaryBadge: {
      icon: "celebration" as const,
      color: "white",
      text: toString(partnershipBadge?.title),
      description: toString(partnershipBadge?.description),
      transparent: false
    },
    regularBadges:
      ordinaryBadges?.splice(0, 3)?.map(({ color, title }) => ({
        color,
        text: title,
        transparent: true
      })) ?? [],
    advertisementBadge: {
      color: toString(advertisementBadge?.color),
      text: toString(advertisementBadge?.title),
      transparent: false
    },
    hotelDetail: {
      cityId: data.room_data.property.city.id,
      address: [
        data.room_data.property.village?.title,
        data.room_data.property.state?.title,
        data.room_data.property.city?.title
      ]
        .filter(Boolean)
        .join(", "),
      isAvailable: data.room_data.is_active,
      hotelTitle: villaTitle,
      stars: 0,
      rating: data?.room_data?.property?.rating?.avg,
      reviewCount: data?.room_data?.property?.rating?.count,
      roomTitle: `${data.room_data.capacity} نفر پایه ${
        data.room_data?.extra_capacity > 0
          ? `+ ${data.room_data?.extra_capacity} نفر اضافه`
          : ""
      }`,
      roomCount: 0,
      cityTitle: data.room_data?.property?.city?.title || "",
      propertyId: data.room_data?.property?.id || 0,
      isInstant: data?.room_availability?.instant || false,
      hotelPageUrl: {
        pathname: "/room/[roomId]",
        query: {
          roomId: data.room_data.id || "",
          ...(query["room_capacity"] > data?.room_data?.capacity
            ? {
                extra_capacity:
                  Number(query["room_capacity"]) -
                    Number(data?.room_data?.capacity) || ""
              }
            : {})
        }
      }
    },
    reservationInfo: {
      bedCount: get(query, "room_capacity") || 0,
      nightCount:
        daysBetweenDates(get(query, "date_from"), get(query, "date_to")) || 1
    },
    location: {
      lat: data.room_data?.property?.location?.latitude,
      lng: data.room_data?.property?.location?.longitude
    },
    gallery:
      data?.room_data?.gallery
        ?.map((item, index) => ({
          id: index + 1,
          title: item?.title,
          alt: villaTitle,
          media: `${ENDPOINTS.CDN_IMAGES}${item?.name}`,
          order: item?.order_no || 0
        }))
        .sort((a, b) => a.order - b.order) ||
      []?.splice(0, 5) ||
      [],
    categories: data?.room_data?.categories || [],
    schemaData: {
      description:
        data.room_data?.description || data.room_data?.property?.description,
      occupancy:
        Number(data.room_data?.capacity || 0) +
        Number(data.room_data?.extra_capacity || 0)
    }
  };
};

export const transformVillaListingSchema = (
  data: VillaLocationResponseType,
  index = -1,
  query: {
    room_capacity?: string | number;
    date_to?: string;
    date_from?: string;
  }
): VillaListingCardType => {
  const detailQuery = omitBy(
    query,
    (value, key) =>
      value === undefined ||
      value === null ||
      value === "" ||
      (key === "room_capacity" && toNumber(value) <= 0)
  );
  const advertisementBadge = data?.room_data?.badges?.find(
    ({ type }) => type === "advertisement"
  ) as VillaLocationResponseType["room_data"]["badges"][0];
  const partnershipBadge = data?.room_data?.badges?.find(
    ({ type }) => type === "partnership"
  ) as VillaLocationResponseType["room_data"]["badges"][0];
  const ordinaryBadges = data?.room_data?.badges?.filter(
    ({ type }) => type === "ordinary"
  );
  const property = data.room_data?.property;
  const title = data.room_data.title || property?.title || "";
  const thumbnail = {
    alt: title,
    url: data.room_data?.image
      ? `${ENDPOINTS.CDN_IMAGES}${data.room_data.image}`
      : property?.image
        ? `${ENDPOINTS.CDN_IMAGES}${property.image}`
        : ""
  };
  const gallery =
    data?.room_data?.gallery
      ?.map((item, index) => ({
        id: index + 1,
        title: item?.title,
        alt: title,
        media: `${ENDPOINTS.CDN_IMAGES}${item?.name}`,
        order: item?.order_no || 0
      }))
      .sort((a, b) => a.order - b.order) || [];
  const { adjustments } = data.room_availability;
  const basePriceOffWithoutSnappPro =
    data.room_availability.base_price_off - (adjustments?.snapp_pro ?? 0);
  const {
    price: totalPrice,
    priceOff: totalPriceOff,
    snappProDiscountPercentage
  } = calculateSnappProDisplayPricing({
    price: data.room_availability.total_price,
    priceOff: data.room_availability.total_local_price_off,
    adjustments
  });

  return {
    id: data.room_data.id,
    noFollow: Boolean(data.room_data.seo?.noIndex),
    index,
    advertisementBadge: advertisementBadge
      ? {
          color: toString(advertisementBadge?.color),
          description: toString(advertisementBadge?.description),
          title: toString(advertisementBadge?.title)
        }
      : undefined,
    badges: {
      list: ordinaryBadges,
      snappProDiscountPercent: snappProDiscountPercentage
    },
    link: {
      id: data.room_data.id,
      pathname: "/room/[roomId]",
      query: {
        ...(detailQuery as ParsedUrlQuery),
        roomId: String(data.room_data.id ?? ""),
        ...(Number(query["room_capacity"]) > Number(data?.room_data?.capacity)
          ? {
              extra_capacity: String(
                Number(query["room_capacity"]) -
                  Number(data?.room_data?.capacity)
              )
            }
          : {})
      }
    },
    address: [
      property?.state?.title ? `استان ${property.state.title}` : undefined,
      property?.city?.title
    ]
      .filter(Boolean)
      .join("، "),
    capacity: {
      capacity: data?.room_data?.capacity || 0,
      extraCapacity: data.room_data?.extra_capacity || 0
    },
    media: {
      gallery,
      id: data.room_data.id,
      thumbnail,
      isAboveFoldImage:
        index >= 0 && index < VILLA_INITIAL_ABOVE_FOLD_IMAGE_COUNT,
      isPriorityImage: index === 0,
      badges: {
        instant: data?.room_availability?.instant,
        partnership: {
          color: "white",
          text: toString(partnershipBadge?.title),
          description: toString(partnershipBadge?.description),
          transparent: false
        }
      },
      title
    },
    title,
    rating: {
      rating: data?.room_data?.property?.rating?.avg,
      reviewCount: data?.room_data?.property?.rating?.count
    },
    reservationInfo: {
      guestsCount: toNumber(get(query, "room_capacity") || 0),
      nightCount:
        daysBetweenDates(get(query, "date_from"), get(query, "date_to")) || 1
    },
    pricing: {
      price: data?.room_availability?.base_price || 0,
      priceOff: basePriceOffWithoutSnappPro,
      totalPrice,
      totalPriceOff
    },
    location: {
      lat: toNumber(data?.room_data?.property?.location?.latitude),
      lng: toNumber(data?.room_data?.property?.location?.longitude)
    },
    propertyId: property?.id ?? data.room_data?.property_id,
    cityId: property?.city?.id,
    cityTitle: property?.city?.title
  };
};

export const fetchPropertyDynamicFilters = (
  geoType: string,
  slug: string,
  date_from: string,
  date_to: string,
  geo: boolean,
  nearby?: boolean
) => {
  if (geoType === "undefined" || slug === "undefined") {
    return;
  }

  return fetcher({
    url: ENDPOINTS.VILLA_PROPERTY_DYNAMIC_FILTERS,
    params: {
      slug,
      geoType,
      geo,
      nearby: nearby || false,
      ...(date_from !== "undefined" && { from: date_from }),
      ...(date_to !== "undefined" && { to: date_to })
    },
    method: "GET"
  })
    .then(({ data }) => ({
      accommodations: data?.accommodations?.map((accommodation) => ({
        label: accommodation?.title,
        type: accommodation?.type
      })),
      categories: data?.categories?.map((category) => ({
        id: category?.id,
        title: category?.name
      })),
      propertyCategories: data?.property_categories?.map((category) => ({
        id: category?.id,
        title: category?.title,
        slug: category?.slug,
        icon: category?.icon
      })),
      facilities: data?.amenities?.map((facility) => ({
        id: facility?.id,
        title: facility?.title,
        icon: facility?.icon
      })),
      rates: data?.rates?.reverse() || [],
      priceRange: {
        min: data?.price_range?.min ?? 100_000,
        max: data?.price_range?.max ?? 1_550_000
      },
      geo_property_summary: data?.geo_property_summary?.map((geo) => ({
        geo_slug: geo?.geo_slug,
        geo_type: geo?.geo_type,
        property_count: geo?.property_count,
        title: geo?.title
      })),
      nearby: data?.nearby,
      instant: data?.instant,
      snapp_pro: data?.snapp_pro
    }))
    .catch(() => ({
      accommodations: [],
      categories: [],
      propertyCategories: [],
      facilities: [],
      rates: [],
      priceRange: {
        min: 100_000,
        max: 1_550_000
      },
      geo_property_summary: [],
      nearby: false,
      instant: false,
      snapp_pro: false
    }));
};

const getQueryValue = (value: ParsedUrlQuery[string]): string =>
  Array.isArray(value) ? toString(value[0]) : toString(value);

const getCookieValue = (cookieHeader: string | undefined, key: string) => {
  const cookie = cookieHeader
    ?.split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${key}=`));

  return cookie ? decodeURIComponent(cookie.slice(key.length + 1)) : "";
};

const getServerSideChannel = (
  req: GetServerSidePropsContext["req"],
  query: ParsedUrlQuery
): Channels => {
  const [host] = toString(req.headers.host).split(":");
  const queryChannel = getQueryValue(query[COOKIE_CHANNEL_NAME]);
  const temporaryChannel = getQueryValue(query[COOKIE_CHANNEL_NAME_TEMPORARY]);
  const cookieChannel = getCookieValue(req.headers.cookie, COOKIE_CHANNEL_NAME);

  return fixChannel(
    (fixedChannelHosts[host] ||
      queryChannel ||
      temporaryChannel ||
      process.env.CHANNEL ||
      cookieChannel) as Channels
  );
};

export const fetchBannersData = (
  page: string,
  type: string,
  channel: Channels = getChannel()
) => {
  const bannerChannel = convertChannelPlatform(channel);

  return fetcher({
    url: ENDPOINTS.GET_BANNERS,
    method: "GET",
    headers: {
      channel
    },
    params: {
      channel: bannerChannel,
      product: "villa-domestic",
      page,
      type
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.GET_BANNERS, bannerChannel, "villa-domestic", page, type]
    }
  })
    .then(({ data }) => data)
    .catch(() => []);
};

export const fetchVillagesData = (
  _query: unknown,
  seoCategory?: Pick<ResolvedVillaSeoCategory, "backendSlug" | "id">
): Promise<VillaListingResponseType | null> => {
  const routeQuery = isSSR()
    ? pick(_query, ["locationType", "cityTitle"])
    : (_query as Record<string, unknown>);
  const { aid, sid } = getTracking();
  const query = applyVillaSeoCategoryDefault(routeQuery, seoCategory);
  const listingEndpoint = seoCategory
    ? ENDPOINTS.VILLA_SEARCH_LOCATION_CATEGORY
    : ENDPOINTS.VILLA_SEARCH_LOCATION;
  const url = replaceOptions(listingEndpoint, {
    cityTitle: get(query, "cityTitle"),
    locationType: get(query, "locationType"),
    categorySlug: seoCategory?.backendSlug
  });

  const categories = toString(get(query, "categories")).split(",");
  const category_ids = categories.filter(
    (item) => item !== "instant" && item !== "snapp_pro"
  );
  const hasInstant = categories.includes("instant");
  const hasSnappPro = categories.includes("snapp_pro");
  const geo_property_summary = toString(
    get(query, "geo_property_summary")
  ).split(",");
  const geoTypes = [];
  const geoSlugs = [];
  if (geo_property_summary)
    for (const item of geo_property_summary) {
      if (item) {
        const [slug, geo_type] = item.split("___");
        geoSlugs.push(slug);
        geoTypes.push(geo_type);
      }
    }
  const parameters = omitBy(
    {
      from: get(query, "date_from") || today(),
      pageSize: VILLA_LIST_PAGE_SIZE,
      to: get(query, "date_to") || tomorrow(),
      priceGT: get(query, "price_from"),
      priceLT: get(query, "price_to"),
      type: get(query, "type"),
      instant: hasInstant,
      snappPro: hasSnappPro,
      availability: get(query, "room_count") || 1,
      nearby: get(query, "nearby") || false,
      version: get(query, "version") || "room",
      capacity: get(query, "room_capacity") || 1,
      page: toNumber(get(query, "page") || 1),
      q: get(query, "q"),
      amenities: toString(get(query, "facilities")).replaceAll("-", ","),
      rate: get(query, "rate") === "all" ? "" : get(query, "rate"),
      ...(get(query, "order_by") ? { order: get(query, "order_by") } : {}),
      category_ids: category_ids.join(","),
      property_categories: get(query, "property_categories"),
      channel: getChannel(),
      geo_slug: geoSlugs.join(","),
      geo_type: geoTypes.join(",")
    },
    (value) => !value
  );
  if (isInvalidCheckin(parameters.from, parameters.to)) {
    return Promise.resolve(null);
  }
  return fetcher({
    method: "GET",
    url,
    params: parameters,
    headers: {
      aid: toString(aid),
      sid: toString(sid)
    },
    next: {
      revalidate: 300,
      tags: [url, JSON.stringify(parameters)]
    }
  })
    .then(({ data }) => data as unknown as VillaListingResponseType)
    .catch(() => null);
};

export const fetchLocationData = (locationType: string, cityTitle: string) => {
  const urls = {
    city: ENDPOINTS.SEARCH_CITY_SLUG,
    state: ENDPOINTS.SEARCH_STATE_SLUG,
    village: ENDPOINTS.SEARCH_VILLAGE_SLUG,
    area: ENDPOINTS.SEARCH_AREA_SLUG,
    category: ENDPOINTS.SEARCH_CATEGORY_SLUG
  };

  if (!urls[locationType]) {
    return {
      id: -1,
      url: "",
      title: "",
      location: null
    };
  }

  return fetcher({
    url: urls[locationType],
    options: {
      slug: cityTitle
    },
    next: {
      revalidate: 300,
      tags: [urls[locationType], cityTitle]
    }
  })
    .then(({ data }) => ({
      id: data?.id ?? "",
      url: data?.slug ?? "",
      title: data?.title ?? "",
      location: data?.location || {},
      state: data?.state ?? {},
      type: locationType
    }))
    .catch(() => ({
      id: -1,
      url: "",
      title: "",
      location: null
    }));
};

export const fetchVillaSeoCategory = async (
  publicSlug: string
): Promise<ResolvedVillaSeoCategory | null> => {
  const backendSlug = getVillaSeoCategoryBackendSlug(publicSlug);
  if (!backendSlug) return null;

  return fetcher({
    url: ENDPOINTS.SEARCH_CATEGORY_SLUG,
    options: { slug: backendSlug },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.SEARCH_CATEGORY_SLUG, backendSlug]
    }
  })
    .then(({ data }) =>
      resolveVillaSeoCategory(publicSlug, data as VillaSeoCategoryRecord)
    )
    .catch(() => null);
};

export const fetchSearchTextData = (q: string) =>
  fetcher({
    url: ENDPOINTS.CITY_AUTOCOMPLETE,
    params: {
      q: q?.replaceAll(/[!"#$%&()*,.:<>?@^{|}]/g, "")
    }
  }).then(({ data }) => {
    const result = data as VillaSearchTextType;
    return (result?.geo || []).map(
      (item) =>
        ({
          id: toString(item?.id),
          title: item?.title,
          url: item?.slug,
          hotel_count:
            result?.properties?.filter(
              ({ city_id, village_id, state_id }) =>
                (item?.entity === "VILLAGE" && item.id === village_id) ||
                (item?.entity === "CITY" && item.id === city_id) ||
                (item?.entity === "STATE" && item.id === state_id)
            ).length || 0,
          info: "",
          stars: 0,
          isHotel: false,
          subtitle: item.entity,
          type: item?.entity?.toLowerCase(),
          location: item?.location
        }) as VillaSearchTextItemType
    );
  });

export const fetchInstalmentWallet = (): Promise<{
  isEligible: boolean;
  showDownPayment: boolean;
  markup: number;
}> =>
  fetcher({
    url: ENDPOINTS.INSTALMENT_WALLET,
    method: "GET",
    headers: {
      product: "VILLA"
    }
  }).then(({ data }) => ({
    isEligible: data?.is_eligible,
    showDownPayment: data?.show_down_payment,
    markup: data?.markup
  }));

export const fetchHotelRateData = (hotelId: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_RATE,
    options: { hotelId },
    method: "GET",
    headers: {
      product: "villa"
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.HOTEL_RATE, toString(hotelId)]
    }
  }).then(({ data }) => {
    const _data = data as HotelVillaRateDataType;
    return {
      rateDetail: {
        clean_rate: _data?.cleaning || "",
        facility_rate: _data?.facilities || "",
        food_quality_rate: _data?.match || "",
        location_rate: _data?.location || "",
        staff_rate: _data?.friendly || "",
        value_for_money_rate: _data?.priceQuality || ""
      },
      rate: Number(_data?.overall).toFixed(1) ?? "",
      rateCount: _data?.rateCount ?? 0,
      offerCount: _data?.recommendationCount ?? 0,
      offerMessage:
        _data?.recommendationType === "recommendation"
          ? "۷۵ درصد از میهمانان اخیر، این اقامتگاه رو پیشنهاد کرده اند. "
          : _data?.recommendationType === "fullRate"
            ? "میهمانان در ۵ رزرو اخیر، امتیاز کامل را به اقامتگاه داده اند"
            : _data?.recommendationType
              ? `میهمانان در ۵ رزرو اخیر، به  "${PersianVillaRatings?.[
                  _data?.recommendationType
                ]}" امتیاز کامل داده اند.`
              : "",
      aiSummary: type?.markdown?.toHTML(data?.aiSummary)
    };
  });

export const fetchHotelReviewData = (
  hotelId: unknown,
  page: unknown,
  product: string,
  filters: Record<string, string>
) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW, { hotelId }),
    method: "GET",
    headers: {
      product
    },
    params: {
      ...filters,
      pageSize: 5,
      page
    }
  }).then(({ data }) => {
    const _data = data as HotelVillaReviewDataType;
    return {
      page: toNumber(_data?.page),
      total: _data?.total,
      rateReviews: (_data?.result ?? [])?.map((comment) => ({
        id: comment?.id,
        helpfulSubmitted: comment?.helpfulSubmitted,
        userName: comment?.passenger,
        roomName: comment.roomTitle,
        rate: String(comment?.avgRating || 0),
        registeredDate: comment?.createdAt,
        standNights: comment?.dateCount,
        comment:
          replaceSnappTripBasedOnChannel(
            filterXSS(comment?.comment, {
              whiteList: {},
              stripIgnoreTag: true,
              stripIgnoreTagBody: ["script"]
            })
          ) ?? "",
        strengths: comment?.pros?.filter(Boolean)?.join(" ,") ?? "",
        weakness: comment?.cons?.filter(Boolean)?.join(" ,") ?? "",
        pictures: comment?.images?.map((image, index) => ({
          id: index,
          media: `${process.env["NEXT_PUBLIC_BASE_URL_IMAGES"]}/reviews/${image}`,
          title: ""
        }))
      }))
    };
  });

const getSeoCategoryFallbackMetadata = (
  locationTitle: string,
  categoryTitle: string
) => ({
  title: `اجاره ${categoryTitle} در ${locationTitle} | رزرو آنلاین ویلا و اقامتگاه`,
  description: `رزرو و اجاره ${categoryTitle} در ${locationTitle} با بهترین قیمت، پشتیبانی ۲۴ ساعته و امکان پرداخت اقساطی.`,
  pageTitle: `اجاره ${categoryTitle} در ${locationTitle}`
});

const applySeoCategoryMetadataDefaults = (
  seoData: (CMSSeoContentData & { resolvedUrl?: string }) | undefined,
  pageRoute: string,
  canonicalRoute: string,
  locationTitle: string,
  categoryTitle: string
): CMSSeoContentData & { resolvedUrl: string } => {
  const fallback = getSeoCategoryFallbackMetadata(locationTitle, categoryTitle);
  const resolvedUrl = seoData?.resolvedUrl || `${getOrigin()}${pageRoute}`;

  return {
    route: seoData?.route || pageRoute,
    title: seoData?.title || fallback.title,
    fallbackTitle: seoData?.fallbackTitle || fallback.title,
    description: seoData?.description || fallback.description,
    fallbackDescription: seoData?.fallbackDescription || fallback.description,
    canonical: `${getOrigin()}${canonicalRoute}`,
    robots: seoData?.robots || "noindex,nofollow",
    redirect: seoData?.redirect || null,
    pageTitle: seoData?.pageTitle || fallback.pageTitle,
    seoContent: seoData?.seoContent || "",
    pageID: seoData?.pageID || "",
    channel: seoData?.channel || null,
    FAQ: seoData?.FAQ || [],
    links: seoData?.links || [],
    resolvedUrl
  };
};

export const loadVillaListingPage = async (
  { query, req, res }: GetServerSidePropsContext,
  categorySlug = ""
): Promise<
  | { props: CityPagePropertiesType }
  | { redirect: { destination: string; permanent: boolean } }
> => {
  const channel = getServerSideChannel(req, query);
  const [locationData, seoCategory] = await Promise.all([
    fetchLocationData(
      getQueryValue(query.locationType),
      getQueryValue(query.cityTitle)
    ),
    categorySlug
      ? fetchVillaSeoCategory(categorySlug)
      : Promise.resolve(undefined)
  ]);

  if (Number(locationData?.id) <= 0 || (categorySlug && !seoCategory)) {
    res.statusCode = 410;
    return { props: {} };
  }

  const locationRoute = getVillaSeoCategoryCanonicalRoute(
    process.env.NEXT_PUBLIC_BASE_PATHNAME,
    getQueryValue(query.locationType),
    getQueryValue(query.cityTitle)
  );
  const pageRoute = `${locationRoute}${
    seoCategory ? `/category/${seoCategory.publicSlug}` : ""
  }`;
  const cmsSeoData = await ((
    ["website", "snapp-ir"] as Array<Channels>
  ).includes(channel)
    ? getCMSSeoContent(
        pageRoute,
        seoCategory
          ? getSeoCategoryFallbackMetadata(
              locationData.title,
              seoCategory.title
            ).title
          : undefined,
        seoCategory
          ? getSeoCategoryFallbackMetadata(
              locationData.title,
              seoCategory.title
            ).description
          : undefined
      )
    : Promise.resolve(undefined));
  const seoData = seoCategory
    ? applySeoCategoryMetadataDefaults(
        cmsSeoData,
        pageRoute,
        locationRoute,
        locationData.title,
        seoCategory.title
      )
    : cmsSeoData;

  if (seoData?.redirect) {
    return { redirect: { destination: seoData?.redirect, permanent: true } };
  }

  const [villagesData, smallBanners, staticBanners] = await Promise.all([
    isEmpty(omit(query, VILLA_LOCATION_ROUTE_QUERY_KEYS))
      ? fetchVillagesData(query, seoCategory)
      : Promise.resolve(null),
    fetchBannersData("Citypage", "small", channel),
    fetchBannersData("Citypage", "static", channel)
  ]);
  return {
    props: {
      seoData: seoData || null,
      locationData,
      villagesData,
      ...(seoCategory ? { seoCategory } : {}),
      smallBanners,
      staticBanners
    }
  };
};

export const getVillaSeoCategoryServerSideProps: GetServerSideProps<
  CityPagePropertiesType
> = (context) =>
  loadVillaListingPage(context, getQueryValue(context.query.categorySlug));

export const getServerSideProps: GetServerSideProps<CityPagePropertiesType> = (
  context
) => loadVillaListingPage(context);
